import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import { useManagementCompanyLogoBlobUrl } from '../../wrh/reports/useManagementCompanyLogo';
import { moduleConfig } from '../config';
import {
  exportMgaAllotmentRemittance,
  exportMgaBondedByCrew,
  remittanceRowsFromRequests,
} from '../reportExports';
import {
  useMgaBondedLines,
  useMgaCashEntries,
  useMgaCrewRequests,
  useMgaPeriod,
  useMgaRemittance,
} from '../useData';
import { BankCsvReport } from './BankCsvReport';
import { BondedByCrewReport } from './BondedByCrewReport';
import { BulkPayslipsReport } from './BulkPayslipsReport';
import { CashBookReport } from './CashBookReport';
import { ClosePackReport } from './ClosePackReport';
import { RemittanceReport } from './RemittanceReport';
import { isMgaReportKey, MGA_REPORT_META } from './reportMeta';
import { ReportShell } from './ReportShell';

type ManagementCompanyRow = {
  id: number;
  name: string;
  isActive?: boolean;
  logoStorageKey?: string | null;
};

const ReportPage: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { id, reportKey } = useParams<{ id: string; reportKey: string }>();
  const periodId = id ? parseInt(id, 10) : NaN;
  const key = isMgaReportKey(reportKey) ? reportKey : null;

  const { item, isLoading: periodLoading } = useMgaPeriod(
    Number.isInteger(periodId) ? periodId : 0,
  );
  const { data: cash, isLoading: cashLoading } = useMgaCashEntries(
    Number.isInteger(periodId) ? periodId : 0,
  );
  const { data: bonded, isLoading: bondedLoading } = useMgaBondedLines(
    Number.isInteger(periodId) ? periodId : 0,
  );
  const { data: requests, isLoading: reqLoading } = useMgaCrewRequests(
    Number.isInteger(periodId) ? periodId : 0,
  );
  const { data: remittance, isLoading: remLoading } = useMgaRemittance(
    Number.isInteger(periodId) ? periodId : 0,
  );
  const { data: companiesRaw } = useSWR('/management-companies?page=1&limit=50', fetcher, {
    revalidateOnFocus: false,
  });

  const managementCompany = useMemo(() => {
    const list = normalizePaginatedListResponse<ManagementCompanyRow>(companiesRaw, 1, 50).data;
    const active = list.filter((c) => c.isActive !== false);
    const withLogo = active.find((c) => c.logoStorageKey) ?? list.find((c) => c.logoStorageKey);
    return withLogo?.logoStorageKey
      ? { id: withLogo.id, name: withLogo.name, logoStorageKey: withLogo.logoStorageKey }
      : null;
  }, [companiesRaw]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  const [downloading, setDownloading] = useState(false);

  const remittanceRows = useMemo(() => {
    if (!item) return [];
    if (remittance.length > 0) return remittance;
    return remittanceRowsFromRequests(requests, item.lines ?? []);
  }, [item, remittance, requests]);

  const lines = item?.lines ?? [];
  const loading =
    periodLoading || cashLoading || bondedLoading || reqLoading || remLoading;

  if (!Number.isInteger(periodId) || !key) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>Report not found</h1>
        <Link to={moduleConfig.module.route.list} className="mt-4 inline-block text-sm underline">
          Back to MGA
        </Link>
      </div>
    );
  }

  const meta = MGA_REPORT_META[key];

  if (!loading && !item) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>Period not found</h1>
        <Link to={moduleConfig.module.route.list} className="mt-4 inline-block text-sm underline">
          Back to MGA
        </Link>
      </div>
    );
  }

  const empty =
    !loading &&
    !!item &&
    ((key === 'payslips' && lines.length === 0) ||
      (key === 'remittance' && remittanceRows.length === 0) ||
      (key === 'bank-csv' && remittanceRows.length === 0) ||
      (key === 'cash-book' && cash.length === 0) ||
      (key === 'bonded-by-crew' && !bonded.some((l) => l.section === 'EXPENSE')));

  const handleDownload = async () => {
    if (!item) return;
    setDownloading(true);
    try {
      if (key === 'remittance') {
        await exportMgaAllotmentRemittance(item, remittanceRows, 'excel');
        showSuccess('Allotment remittance Excel downloaded.');
      } else if (key === 'bank-csv') {
        await exportMgaAllotmentRemittance(item, remittanceRows, 'csv');
        showSuccess('Allotment bank CSV downloaded.');
      } else if (key === 'bonded-by-crew') {
        await exportMgaBondedByCrew({ period: item, cash, bonded, requests }, 'excel');
        showSuccess('Bonded-by-crew Excel downloaded.');
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const downloadLabel =
    meta.download === 'excel' ? 'Download Excel' : meta.download === 'csv' ? 'Download CSV' : undefined;

  const logoAlt = managementCompany?.name || 'Management company logo';

  return (
    <ReportShell
      periodId={periodId}
      title={meta.title}
      subtitle={meta.subtitle}
      loading={loading || !item}
      empty={empty}
      emptyMessage={
        key === 'payslips'
          ? 'No crew lines yet. Generate from the vessel crew list first.'
            : key === 'cash-book'
            ? 'No Income / CTM or Cash out entries for this period.'
            : key === 'bonded-by-crew'
              ? 'No Stock out lines for this period.'
              : 'No allotment remittance rows for this period.'
      }
      downloadLabel={downloadLabel}
      onDownload={meta.download ? handleDownload : undefined}
      downloading={downloading}
    >
      {item && key === 'close-pack' ? (
        <ClosePackReport
          period={item}
          cash={cash}
          bonded={bonded}
          requests={requests}
          companyLogoSrc={companyLogoSrc}
          companyLogoAlt={logoAlt}
        />
      ) : null}
      {item && key === 'payslips' ? (
        <BulkPayslipsReport
          period={item}
          lines={lines}
          companyLogoSrc={companyLogoSrc}
          companyLogoAlt={logoAlt}
        />
      ) : null}
      {item && key === 'remittance' ? (
        <RemittanceReport
          period={item}
          rows={remittanceRows}
          companyLogoSrc={companyLogoSrc}
          companyLogoAlt={logoAlt}
        />
      ) : null}
      {item && key === 'bank-csv' ? (
        <BankCsvReport
          period={item}
          rows={remittanceRows}
          companyLogoSrc={companyLogoSrc}
          companyLogoAlt={logoAlt}
        />
      ) : null}
      {item && key === 'cash-book' ? (
        <CashBookReport
          period={item}
          cash={cash}
          companyLogoSrc={companyLogoSrc}
          companyLogoAlt={logoAlt}
        />
      ) : null}
      {item && key === 'bonded-by-crew' ? (
        <BondedByCrewReport
          period={item}
          bonded={bonded}
          companyLogoSrc={companyLogoSrc}
          companyLogoAlt={logoAlt}
        />
      ) : null}
    </ReportShell>
  );
};

export default ReportPage;
